import React, {useId, useState} from 'react';
import {
    ClockCircleOutlined,
    DownOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import './ReferenceReviewCard.css';

const EMPTY_LIST = [];
const EMPTY_MAP = {};

const getSnapshotPersisted = (review = {}) => {
    const terminal = review.terminal || {};
    return review.snapshot_persisted
        ?? terminal.snapshot_persisted
        ?? terminal.audit?.snapshot_persisted
        ?? terminal.audit?.snapshot_persistence?.persisted;
};

export default function ReferenceReviewProgressNotice({review = {}, streaming = false}) {
    const {t} = useTranslation('review');
    const [showAllRequirements, setShowAllRequirements] = useState(false);
    const requirementListId = useId();
    const requirementOrder = review.requirement_order || EMPTY_LIST;
    const requirements = review.requirements_by_id || EMPTY_MAP;
    const verdicts = review.verdicts_by_id || EMPTY_MAP;
    const pendingRequirements = requirementOrder.filter((requirementId) => (
        !verdicts[requirementId]
    ));
    const visibleRequirements = showAllRequirements
        ? pendingRequirements
        : pendingRequirements.slice(0, 4);
    const hiddenRequirementCount = Math.max(0, pendingRequirements.length - 4);
    const persisted = getSnapshotPersisted(review);
    const completionStatus = review.completion_status || review.terminal?.completion_status;
    const outcomeCode = review.outcome_code || review.terminal?.outcome_code;
    const coverageComplete = review.coverage_complete ?? review.terminal?.coverage_complete;
    const failedAttempt = review.status === 'failed'
        || completionStatus === 'failed'
        || outcomeCode === 'failed';
    const partialAttempt = completionStatus === 'partial'
        || outcomeCode === 'partial'
        || coverageComplete === false;
    const showProgress = (streaming || failedAttempt || partialAttempt) && (
        ['idle', 'extracting', 'verifying'].includes(review.status)
        || requirementOrder.length > 0
    );
    const showPersistenceWarning = !failedAttempt
        && !streaming
        && persisted === false
        && Object.keys(verdicts).length > 0;
    const showPartialWarning = !streaming
        && !failedAttempt
        && requirementOrder.length > 0
        && partialAttempt;
    const hydrationUnavailable = ['error', 'incompatible'].includes(review.hydration_status);

    if (
        !showProgress
        && !showPersistenceWarning
        && !showPartialWarning
        && !hydrationUnavailable
        && !failedAttempt
    ) return null;

    return (
        <div className="reference-review-notices">
            {showProgress && (
                <section
                    className="reference-review-progress"
                    aria-label={t('referenceReview.progressTitle')}
                >
                    <div className="reference-review-progress__header" role="status">
                        <ClockCircleOutlined aria-hidden="true" />
                        <div>
                            <strong>{t('referenceReview.progressTitle')}</strong>
                            <span>{t('referenceReview.progressCount', {
                                extracted: requirementOrder.length,
                                reviewed: Object.keys(verdicts).length,
                            })}</span>
                        </div>
                    </div>
                    <div className="reference-review-progress__items" id={requirementListId}>
                        {visibleRequirements.map((requirementId) => (
                                <div className="is-pending" key={requirementId}>
                                    <span className="reference-review-progress__requirement-id">
                                        {requirementId}
                                    </span>
                                    <p>{requirements[requirementId]?.title || requirementId}</p>
                                    <small>
                                        <ClockCircleOutlined aria-hidden="true" />
                                        {t('referenceReview.pending')}
                                    </small>
                                </div>
                        ))}
                    </div>
                    {hiddenRequirementCount > 0 && (
                        <button
                            type="button"
                            className="reference-review-progress__remaining"
                            onClick={() => setShowAllRequirements((current) => !current)}
                            aria-expanded={showAllRequirements}
                            aria-controls={requirementListId}
                            aria-label={showAllRequirements
                                ? t('referenceReview.showFewerRequirements')
                                : t('referenceReview.showRemainingRequirements', {
                                    count: hiddenRequirementCount,
                                })}
                        >
                            <span>
                                {showAllRequirements
                                    ? t('referenceReview.showFewerRequirements')
                                    : t('referenceReview.pendingRemaining', {
                                        count: hiddenRequirementCount,
                                    })}
                            </span>
                            <DownOutlined
                                className={showAllRequirements ? 'is-open' : ''}
                                aria-hidden="true"
                            />
                        </button>
                    )}
                </section>
            )}
            {showPersistenceWarning && (
                <p className="reference-review-persistence-warning" role="alert">
                    <ExclamationCircleOutlined aria-hidden="true" />
                    {t('referenceReview.notPersisted')}
                </p>
            )}
            {showPartialWarning && (
                <p className="reference-review-partial-warning" role="status">
                    <ExclamationCircleOutlined aria-hidden="true" />
                    {t('referenceReview.partialCoverage')}
                </p>
            )}
            {hydrationUnavailable && (
                <p className="reference-review-hydration-warning" role="alert">
                    <ExclamationCircleOutlined aria-hidden="true" />
                    {review.hydration_status === 'incompatible'
                        ? t('referenceReview.snapshotIncompatible')
                        : t('referenceReview.hydrationError')}
                </p>
            )}
            {failedAttempt && (
                <p className="reference-review-failed-run-warning" role="alert">
                    <ExclamationCircleOutlined aria-hidden="true" />
                    {review.persisted_snapshot
                        ? t('referenceReview.failedAttemptWithSnapshot')
                        : t('referenceReview.failedAttempt')}
                </p>
            )}
        </div>
    );
}
